import React from 'react';
import { useTranslation } from 'react-i18next';
import isEmail from 'validator/lib/isEmail';

import { updateRequestSamplesForm } from 'Apollo/Store/Showroom/UserView/Samples/Form/resolvers';
import { setIsRequestSamplesValid } from 'Apollo/Store/Showroom/UserView/Samples/resolvers';
import { useGetShowroomSamplesFormQuery } from 'generated/graphql';

import { UserConsentHookResponse } from 'lib/userConsent';

import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/PhoneAndroid';

import { useStyles } from './styles';

export type ComponentProps = {
	renderUserConsentInput: UserConsentHookResponse['renderInput'];
};

const ShowroomSamplesFormStepsAboutRequest: React.FC<ComponentProps> = props => {
	const { renderUserConsentInput } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const {
		data: {
			showroom: {
				userView: {
					samples: {
						form: { firstName, lastName, jobTitle, company, email, phoneNumber }
					}
				}
			}
		}
	} = useGetShowroomSamplesFormQuery({ returnPartialData: true });

	React.useEffect(() => {
		if (!firstName || !lastName || !jobTitle || !company || !isEmail(email) || !phoneNumber) setIsRequestSamplesValid(false);
		else setIsRequestSamplesValid(true);
	}, [company, email, firstName, jobTitle, lastName, phoneNumber]);

	const [emailIsValid, setEmailIsValid] = React.useState(true);

	return (
		<div className={classes.root}>
			<div className={classes.header}>{t('showroom.samples.form.reachYou')}</div>
			<div className={classes.aboutYouBox}>
				<div className={classes.aboutYouTitle}>{t('showroom.samples.form.usefulInfo')}</div>
				<TextField
					id="firstNameInput"
					defaultValue={firstName}
					onChange={ev => updateRequestSamplesForm({ firstName: ev.target.value })}
					size="small"
					className={classes.aboutYouInput}
					variant="outlined"
					label={t('showroom.samples.form.firstName')}
					fullWidth
				/>
				<TextField
					id="lastNameInput"
					defaultValue={lastName}
					onChange={ev => updateRequestSamplesForm({ lastName: ev.target.value })}
					size="small"
					className={classes.aboutYouInput}
					variant="outlined"
					label={t('showroom.samples.form.lastName')}
					fullWidth
				/>
				<TextField
					id="jobTitleInput"
					defaultValue={jobTitle}
					onChange={ev => updateRequestSamplesForm({ jobTitle: ev.target.value })}
					size="small"
					className={classes.aboutYouInput}
					variant="outlined"
					label={t('showroom.samples.form.jobTitle')}
					fullWidth
				/>
				<TextField
					id="companyInput"
					defaultValue={company}
					onChange={ev => updateRequestSamplesForm({ company: ev.target.value })}
					size="small"
					className={classes.aboutYouInput}
					variant="outlined"
					label={t('showroom.samples.form.company')}
					fullWidth
				/>
				<TextField
					id="emailInput"
					defaultValue={email}
					onChange={ev => updateRequestSamplesForm({ email: ev.target.value })}
					size="small"
					className={classes.aboutYouInput}
					variant="outlined"
					label={t('showroom.samples.form.email')}
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<EmailIcon />
							</InputAdornment>
						)
					}}
					error={!emailIsValid}
					onBlur={ev => {
						if (isEmail(ev.target.value)) setEmailIsValid(true);
						else setEmailIsValid(false);
					}}
					helperText={!emailIsValid ? t('showroom.samples.form.enterValidEmail') : null}
					FormHelperTextProps={{ error: !emailIsValid }}
				/>
				<TextField
					id="phoneNumberInput"
					defaultValue={phoneNumber}
					onChange={ev => updateRequestSamplesForm({ phoneNumber: ev.target.value })}
					size="small"
					className={classes.aboutYouInput}
					variant="outlined"
					label={t('showroom.samples.form.phoneNumber')}
					fullWidth
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<PhoneIcon />
							</InputAdornment>
						)
					}}
				/>
				{renderUserConsentInput({ wrapper: { className: classes.userConsentWrapper } })}
			</div>
		</div>
	);
};

export default React.memo(ShowroomSamplesFormStepsAboutRequest);
